import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import BottomNav from '../../components/BottomNav'
import Spinner from '../../components/Spinner'
import { getUserPayments } from '../../api/users'
import { useAuth } from '../../context/AuthContext'

const G = '#C89B4C'
const BD = 'rgba(244,238,225,0.14)'
const S = 'linear-gradient(155deg, rgba(255,248,235,0.05) 0%, rgba(255,248,235,0.015) 100%)'
const GLASS = { background: S, backdropFilter: 'blur(11px) saturate(150%)', WebkitBackdropFilter: 'blur(11px) saturate(150%)' } as const
const TX = '#F4F1EA'
const TX2 = '#C8C4BB'
const TX3 = '#8A8480'
const SERIF = "'Newsreader', Georgia, serif"

const METHOD_LABELS: Record<string, string> = {
  cash: '💵 Наличными / Naqd',
  transfer: '🏦 Перевод / O\'tkazma',
}

function formatMoney(n: number) {
  return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)}`
}

function formatDate(s: string) {
  if (!s) return '—'
  const d = new Date(s)
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function PaymentHistoryPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const lang = user?.language ?? 'ru'

  const { data: payments, isLoading } = useQuery({
    queryKey: ['my-payments'],
    queryFn: () => getUserPayments({ limit: 200 }),
  })

  const total = payments?.reduce((sum, p) => sum + p.paid_amount, 0) ?? 0

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 40, padding: '10px 20px',
        background: 'rgba(13,21,38,0.82)', backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)', borderBottom: `1px solid ${BD}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: TX3, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <span style={{ fontSize: 11, fontWeight: 600, color: TX3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {lang === 'uz' ? "To'lovlar tarixi" : 'История платежей'}
        </span>
      </header>

      <main style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 104px' }}>
        <h1 style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 30, color: TX, margin: '0 0 6px', lineHeight: 1.1 }}>
          {lang === 'uz' ? "To'lovlar" : 'Платежи'}
        </h1>
        <p style={{ margin: '0 0 24px', fontSize: 13, color: TX3 }}>
          {lang === 'uz' ? "Barcha tasdiqlangan to'lovlar" : 'Все подтверждённые оплаты по всем заявкам'}
        </p>

        {/* Total summary */}
        {(payments?.length ?? 0) > 0 && (
          <div style={{
            padding: '16px 20px', borderRadius: 14, marginBottom: 20,
            background: 'rgba(200,155,76,0.08)', border: `1px solid rgba(200,155,76,0.20)`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {lang === 'uz' ? "Jami to'langan" : 'Итого оплачено'}
              </p>
              <p style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 24, color: G, fontWeight: 600 }}>
                {formatMoney(total)}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {lang === 'uz' ? "To'lovlar soni" : 'Транзакций'}
              </p>
              <p style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 22, color: TX }}>
                {payments?.length ?? 0}
              </p>
            </div>
          </div>
        )}

        {isLoading ? (
          <Spinner className="mt-10" />
        ) : !payments?.length ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🧾</div>
            <p style={{ fontSize: 14, color: TX3, margin: '0 0 4px' }}>
              {lang === 'uz' ? "Hali to'lovlar yo'q" : 'Платежей пока нет'}
            </p>
            <p style={{ fontSize: 12, color: TX3 }}>
              {lang === 'uz' ? "Tasdiqlangan to'lovlar bu yerda ko'rinadi" : 'Подтверждённые оплаты появятся здесь'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {payments.map(p => (
              <div
                key={p.id}
                onClick={() => navigate(`/orders/${p.order_id}`)}
                style={{
                  padding: '14px 16px', borderRadius: 14, cursor: 'pointer',
                  ...GLASS, border: `1px solid ${BD}`,
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
                }}
              >
                {/* Left */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: '0 0 2px', fontSize: 11, color: TX3, fontFamily: "'JetBrains Mono', monospace" }}>
                    {p.registry_num ?? `#${p.order_id}`} · {lang === 'uz' ? 'oylik' : 'мес.'} #{p.payment_num}
                  </p>
                  {p.product_name && (
                    <p style={{ margin: '0 0 6px', fontSize: 13, color: TX2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.product_name}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, color: '#4CAF7E', background: 'rgba(76,175,126,0.10)', border: '1px solid rgba(76,175,126,0.20)', borderRadius: 99, padding: '2px 8px' }}>
                      ✓ {lang === 'uz' ? "To'langan" : 'Оплачено'}
                    </span>
                    {p.method && (
                      <span style={{ fontSize: 10, color: TX3 }}>
                        {METHOD_LABELS[p.method] ?? p.method}
                      </span>
                    )}
                    {p.paid_at && (
                      <span style={{ fontSize: 10, color: TX3 }}>
                        {formatDate(p.paid_at)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Right */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <p style={{ margin: '0 0 4px', fontFamily: "'JetBrains Mono', monospace", fontSize: 16, color: TX, fontWeight: 600 }}>
                    {formatMoney(p.paid_amount)}
                  </p>
                  {p.paid_amount !== p.amount && (
                    <p style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TX3, textDecoration: 'line-through' }}>
                      {formatMoney(p.amount)}
                    </p>
                  )}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TX3} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 8 }}><path d="M9 18l6-6-6-6"/></svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  )
}
